import React, { useState, useEffect, useMemo } from 'react';
import { Search, Filter, Plus, Download, ExternalLink, Trash2, Globe, X, Info, FileText, Image as ImageIcon, FileSpreadsheet, File } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db, storage } from '../../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  getAccountsByType,
  getAccountById,
  updateAccount,
  getGlobalDocuments,
  addGlobalDocument,
  deleteGlobalDocument
} from '../../services/firestoreService';
import { isKickoffEligibleClient } from '../../utils/crmClientListFilters';
import PortalBackground from '../../components/PortalBackground';

const DOCUMENT_DEPARTMENTS = ['General', 'Sales', 'Operations', 'Technology', 'Finance', 'Marketing', 'HR'];

const UPLOAD_ACCEPT = '.pdf,.jpg,.jpeg,.png,.csv,.xls,.xlsx,.doc,.docx';
const UPLOAD_EXT = new Set(['pdf', 'jpg', 'jpeg', 'png', 'csv', 'xls', 'xlsx', 'doc', 'docx']);
function isUploadFileAllowed(file) {
  if (!file) return false;
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  if (UPLOAD_EXT.has(ext)) return true;
  const t = (file.type || '').toLowerCase();
  if (t.includes('pdf')) return true;
  if (t.startsWith('image/')) return true;
  if (t === 'text/csv' || t === 'application/csv') return true;
  if (t.includes('excel') || t.includes('spreadsheetml.sheet')) return true;
  if (t.includes('msword') || t.includes('wordprocessingml.document')) return true;
  return false;
}

function getFileMeta(docItem) {
  const source = docItem?.fileName || docItem?.name || docItem?.url || '';
  const ext = (source.split('.').pop() || '').toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
    return { Icon: ImageIcon, color: 'text-purple-500', bg: 'bg-purple-100 dark:bg-purple-900/40' };
  }
  if (['csv', 'xls', 'xlsx'].includes(ext)) {
    return { Icon: FileSpreadsheet, color: 'text-green-600', bg: 'bg-green-100 dark:bg-green-900/40' };
  }
  if (ext === 'pdf') {
    return { Icon: FileText, color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/40' };
  }
  if (['doc', 'docx'].includes(ext)) {
    return { Icon: FileText, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/40' };
  }
  return { Icon: File, color: 'text-gray-500', bg: 'bg-gray-100 dark:bg-gray-700' };
}

const accountLabel = (acc) =>
  acc?.name || acc?.company || acc?.contact?.name ||
  [acc?.contact?.firstName, acc?.contact?.lastName].filter(Boolean).join(' ').trim() ||
  'Untitled account';

const CrmCollection = () => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();

  const [mode, setMode] = useState('global'); // 'global' | 'company'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('All');
  const [loading, setLoading] = useState(true);

  const [staffName, setStaffName] = useState('');
  const [globalDocuments, setGlobalDocuments] = useState([]);

  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [companyDocuments, setCompanyDocuments] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(false);

  // Upload modal
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [pendingTitle, setPendingTitle] = useState('');
  const [pendingDescription, setPendingDescription] = useState('');
  const [pendingDepartment, setPendingDepartment] = useState('General');
  const [pendingVisibility, setPendingVisibility] = useState('client'); // company uploads only

  const [descriptionDoc, setDescriptionDoc] = useState(null);

  const selectedAccount = useMemo(
    () => accounts.find((a) => a.id === selectedAccountId) || null,
    [accounts, selectedAccountId]
  );

  // Initial load: staff name, global library, client accounts.
  useEffect(() => {
    const load = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }
      try {
        try {
          const snap = await getDoc(doc(db, 'internal_users', currentUser.uid));
          const data = snap.exists() ? snap.data() : null;
          const name = [data?.firstName, data?.lastName].filter(Boolean).join(' ').trim();
          setStaffName(name || currentUser.displayName || currentUser.email || 'BBA');
        } catch (e) {
          setStaffName(currentUser.displayName || currentUser.email || 'BBA');
        }

        const [globals, clientAccounts] = await Promise.all([
          getGlobalDocuments().catch(() => []),
          getAccountsByType('Clients').catch(() => [])
        ]);
        setGlobalDocuments(globals);
        setAccounts(
          clientAccounts
            .filter(isKickoffEligibleClient)
            .sort((a, b) => accountLabel(a).localeCompare(accountLabel(b)))
        );
      } catch (error) {
        console.error('CrmCollection load error:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser]);

  // Load a company's documents when selected.
  useEffect(() => {
    if (!selectedAccountId) {
      setCompanyDocuments([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setCompanyLoading(true);
      try {
        const account = await getAccountById(selectedAccountId);
        if (!cancelled) setCompanyDocuments(account?.customDocuments || []);
      } catch (error) {
        console.error('Error loading company documents:', error);
        if (!cancelled) setCompanyDocuments([]);
      } finally {
        if (!cancelled) setCompanyLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedAccountId]);

  const sourceDocuments = mode === 'global' ? globalDocuments : companyDocuments;

  const visibleDocuments = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return sourceDocuments.filter((d) => {
      const matchesSearch =
        d.name?.toLowerCase().includes(q) ||
        d.uploader?.toLowerCase().includes(q) ||
        d.uploaderCompany?.toLowerCase().includes(q);
      const matchesDepartment = selectedDepartment === 'All' || d.department === selectedDepartment;
      return matchesSearch && matchesDepartment;
    });
  }, [sourceDocuments, searchQuery, selectedDepartment]);

  const handleViewInNewTab = (docItem) => {
    if (docItem.url) window.open(docItem.url, '_blank', 'noopener,noreferrer');
  };

  const handleDownload = (docItem) => {
    if (!docItem.url) return;
    const link = document.createElement('a');
    link.href = docItem.url;
    link.download = docItem.name || 'document';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async (docItem) => {
    if (mode === 'global') {
      if (!window.confirm('Delete this document from the Global library? Everyone will lose access.')) return;
      try {
        if (docItem.fileName) {
          await deleteObject(ref(storage, `global-documents/${docItem.fileName}`)).catch(() => {});
        }
        await deleteGlobalDocument(docItem.id);
        setGlobalDocuments((prev) => prev.filter((d) => d.id !== docItem.id));
      } catch (error) {
        console.error('Error deleting global document:', error);
        alert('Failed to delete document. Please try again.');
      }
    } else {
      if (!selectedAccountId) return;
      if (!window.confirm('Delete this document from the company collection?')) return;
      try {
        if (docItem.fileName) {
          await deleteObject(ref(storage, `client-documents/${docItem.fileName}`)).catch(() => {});
        }
        const account = await getAccountById(selectedAccountId);
        const updatedDocuments = (account?.customDocuments || []).filter((d) => d.id !== docItem.id);
        await updateAccount(selectedAccountId, { ...account, customDocuments: updatedDocuments });
        setCompanyDocuments(updatedDocuments);
      } catch (error) {
        console.error('Error deleting company document:', error);
        alert('Failed to delete document. Please try again.');
      }
    }
  };

  const handleUploadClick = () => {
    if (mode === 'company' && !selectedAccountId) {
      alert('Choose a company first.');
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = UPLOAD_ACCEPT;
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setPendingFile(file);
        setPendingTitle(file.name.replace(/\.[^/.]+$/, ''));
        setPendingDescription('');
        setPendingDepartment('General');
        setPendingVisibility('client');
        setShowUploadModal(true);
      }
    };
    input.click();
  };

  const resetUploadModal = () => {
    setShowUploadModal(false);
    setPendingFile(null);
    setPendingTitle('');
    setPendingDescription('');
    setPendingDepartment('General');
    setPendingVisibility('client');
  };

  const handleUploadSubmit = async () => {
    const file = pendingFile;
    if (!file) return;
    if (!isUploadFileAllowed(file)) {
      alert('Please select a PDF, image, CSV, Excel, or Word file.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB.');
      return;
    }

    try {
      setUploading(true);
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const documentName = (pendingTitle || file.name.replace(/\.[^/.]+$/, '')).trim() || file.name;

      if (mode === 'global') {
        const fileName = `global-${timestamp}.${fileExtension}`;
        const snapshot = await uploadBytes(ref(storage, `global-documents/${fileName}`), file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        const newDoc = {
          name: documentName,
          description: pendingDescription.trim() || null,
          url: downloadURL,
          fileName,
          uploader: staffName,
          uploaderUserId: currentUser?.uid || null,
          uploaderType: 'internal',
          uploaderCompany: null,
          department: pendingDepartment || null,
          uploadDate: new Date().toISOString()
        };
        const newId = await addGlobalDocument(newDoc);
        setGlobalDocuments((prev) => [{ id: newId, ...newDoc }, ...prev]);
      } else {
        const fileName = `client-${selectedAccountId}-custom-${timestamp}.${fileExtension}`;
        const snapshot = await uploadBytes(ref(storage, `client-documents/${fileName}`), file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        const newDoc = {
          id: timestamp.toString(),
          name: documentName,
          url: downloadURL,
          uploader: staffName,
          uploaderUserId: currentUser?.uid || null,
          uploadDate: new Date().toISOString(),
          fileName,
          visibility: pendingVisibility,
          department: pendingDepartment || null
        };
        const account = await getAccountById(selectedAccountId);
        const updatedDocuments = [...(account?.customDocuments || []), newDoc];
        await updateAccount(selectedAccountId, { ...account, customDocuments: updatedDocuments });
        setCompanyDocuments(updatedDocuments);
      }
      resetUploadModal();
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const departmentOptions = ['All', ...DOCUMENT_DEPARTMENTS];

  const modeTab = (key, label, count) => {
    const active = mode === key;
    return (
      <button
        key={key}
        onClick={() => setMode(key)}
        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
          active
            ? 'bg-red-600 text-white shadow-sm'
            : darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
        }`}
      >
        {key === 'global' && <Globe className="h-4 w-4" />}
        <span>{label}</span>
        {typeof count === 'number' && (
          <span className={`text-xs px-1.5 py-0.5 rounded-full ${
            active ? 'bg-white/20 text-white' : darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-600'
          }`}>
            {count}
          </span>
        )}
      </button>
    );
  };

  const addButtonLabel = mode === 'global'
    ? 'Add to Global'
    : selectedAccount ? `Add to ${accountLabel(selectedAccount)}` : 'Add Document';

  return (
    <div className="min-h-screen relative">
      <PortalBackground />
      <div className="relative max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="relative mb-4">
          <h1 className={`text-3xl font-bold tracking-tight text-center text-gray-900 dark:text-white`}>Collection</h1>
          <button
            onClick={handleUploadClick}
            disabled={mode === 'company' && !selectedAccountId}
            className="absolute right-0 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 px-3.5 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="h-4 w-4" />
            {addButtonLabel}
          </button>
        </div>

        {/* Mode tabs */}
        <div className={`flex flex-wrap justify-center gap-2 p-1 rounded-xl mb-4 mx-auto w-fit bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm`}>
          {modeTab('global', 'Global', globalDocuments.length)}
          {modeTab('company', 'By Company')}
        </div>

        {/* Company selector */}
        {mode === 'company' && (
          <div className="flex justify-center mb-4">
            <select
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              className={`w-full max-w-md px-3 py-2 text-sm border rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 ${
                darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'
              }`}
            >
              <option value="">Select a company…</option>
              {accounts.map((acc) => (
                <option key={acc.id} value={acc.id}>{accountLabel(acc)}</option>
              ))}
            </select>
          </div>
        )}

        {/* Search and department filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              className={`block w-full pl-9 pr-3 py-2 text-sm border rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 ${
                darkMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'border-gray-300 bg-white text-gray-900 placeholder-gray-400'
              }`}
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="relative sm:w-44">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Filter className="h-4 w-4 text-gray-400" />
            </div>
            <select
              className={`block w-full pl-9 pr-3 py-2 text-sm border rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 ${
                darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'
              }`}
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
            >
              {departmentOptions.map((dept) => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Document grid */}
        {loading ? (
          <div className="text-center py-16 text-gray-500 dark:text-gray-400">Loading documents...</div>
        ) : mode === 'company' && !selectedAccountId ? (
          <div className="text-center py-16 text-gray-500 dark:text-gray-400">
            Choose a company above to view their collection.
          </div>
        ) : companyLoading ? (
          <div className="text-center py-16 text-gray-500 dark:text-gray-400">Loading collection...</div>
        ) : visibleDocuments.length === 0 ? (
          <div className={`rounded-xl border border-dashed p-12 text-center ${
            darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-300 text-gray-500'
          }`}>
            <File className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No documents found</p>
            <p className="text-sm mt-1">
              {searchQuery || selectedDepartment !== 'All'
                ? 'Try adjusting your search or filter.'
                : mode === 'global'
                ? 'Add a document to share with everyone.'
                : 'This company has no documents yet.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleDocuments.map((docItem) => {
              const { Icon, color, bg } = getFileMeta(docItem);
              const isInternalOnly = mode === 'company' && docItem.visibility === 'internal';
              return (
                <div
                  key={docItem.id}
                  className={`group rounded-xl border p-4 transition-all hover:shadow-md ${
                    darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${bg}`}>
                      <Icon className={`w-5 h-5 ${color}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className={`text-sm font-semibold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`} title={docItem.name}>
                        {docItem.name}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                        {docItem.uploader || 'Unknown'}
                        {mode === 'global' && docItem.uploaderCompany ? ` · ${docItem.uploaderCompany}` : ''}
                        {docItem.uploadDate ? ` · ${new Date(docItem.uploadDate).toLocaleDateString()}` : ''}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {mode === 'global' && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                            <Globe className="h-2.5 w-2.5" /> Global
                          </span>
                        )}
                        {isInternalOnly && (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                            Internal
                          </span>
                        )}
                        {docItem.department && (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                            {docItem.department}
                          </span>
                        )}
                      </div>
                    </div>
                    {docItem.description && (
                      <button
                        onClick={() => setDescriptionDoc(docItem)}
                        title="View description"
                        className={`flex-shrink-0 -mt-0.5 -mr-0.5 p-1 rounded-md transition-colors ${
                          darkMode ? 'text-gray-500 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <Info className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="flex items-center gap-2 mt-4">
                    <button
                      onClick={() => handleViewInNewTab(docItem)}
                      className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                        darkMode ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <ExternalLink className="h-3.5 w-3.5" /> View
                    </button>
                    <button
                      onClick={() => handleDownload(docItem)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-700 text-white hover:bg-gray-800 transition-colors"
                    >
                      <Download className="h-3.5 w-3.5" /> Download
                    </button>
                    <button
                      onClick={() => handleDelete(docItem)}
                      title="Delete"
                      className={`px-2.5 py-1.5 rounded-lg border transition-colors ${
                        darkMode
                          ? 'border-gray-600 text-gray-400 hover:bg-red-900/30 hover:text-red-400'
                          : 'border-gray-300 text-gray-400 hover:bg-red-50 hover:text-red-600'
                      }`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Description popup */}
      {descriptionDoc && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setDescriptionDoc(null)}>
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 max-w-md w-full shadow-xl`} onClick={(e) => e.stopPropagation()}>
            <div className="relative flex items-center justify-center mb-4">
              <h3 className={`text-base font-semibold text-center px-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{descriptionDoc.name}</h3>
              <button onClick={() => setDescriptionDoc(null)} className="absolute right-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className={`text-sm whitespace-pre-wrap ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{descriptionDoc.description}</p>
          </div>
        </div>
      )}

      {/* Upload modal */}
      {showUploadModal && pendingFile && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 max-w-lg w-full shadow-xl`}>
            <div className="relative flex items-center justify-center mb-5">
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {mode === 'global' ? 'Add to Global' : `Add to ${selectedAccount ? accountLabel(selectedAccount) : 'Company'}`}
              </h3>
              <button onClick={resetUploadModal} className="absolute right-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className={`text-xs mb-4 truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{pendingFile.name}</p>

            <div className="space-y-4 mb-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Title</label>
                <input
                  type="text"
                  value={pendingTitle}
                  onChange={(e) => setPendingTitle(e.target.value)}
                  placeholder="Add a title or leave as filename"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300 bg-white text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>

              {mode === 'global' && (
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Description</label>
                  <textarea
                    value={pendingDescription}
                    onChange={(e) => setPendingDescription(e.target.value)}
                    rows={3}
                    placeholder="Briefly describe this document for everyone"
                    className={`w-full px-3 py-2 text-sm border rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-red-500 ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300 bg-white text-gray-900 placeholder-gray-400'
                    }`}
                  />
                </div>
              )}

              {mode === 'company' && (
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Visibility</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setPendingVisibility('client')}
                      className={`px-3 py-2 text-sm rounded-lg border text-left transition-colors ${
                        pendingVisibility === 'client'
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                          : darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="font-medium block">Client-visible</span>
                      <span className="text-xs opacity-80">Shows in their portal</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingVisibility('internal')}
                      className={`px-3 py-2 text-sm rounded-lg border text-left transition-colors ${
                        pendingVisibility === 'internal'
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                          : darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="font-medium block">Internal only</span>
                      <span className="text-xs opacity-80">Staff can see it</span>
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Department</label>
                <select
                  value={pendingDepartment}
                  onChange={(e) => setPendingDepartment(e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'
                  }`}
                >
                  {DOCUMENT_DEPARTMENTS.map((dept) => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={resetUploadModal}
                className={`px-4 py-2 text-sm rounded-lg border ${
                  darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleUploadSubmit}
                disabled={uploading}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrmCollection;
